import React, { SetStateAction, Dispatch } from "react";
import { useTranslation } from "react-i18next";
import ActionMenu from "components/ActionMenu";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import { isMobileSize, USER_ROLES } from "utils";
import { useNavigate } from "react-router";
import useToast from "hooks/useToast";
import DeleteBuilding from "../BuildingPassport/DeleteBuilding";
import ArchiveBuilding from "../BuildingPassport/ArchiveBuilding";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import ConfirmModal from "components/ConfirmModal";
import { getLocalAuthHeader, userBuildingApi } from "api-client";
import { observer } from "mobx-react";
import { buildingStore } from "store/Building/BuildingStore";
import { ROUTES_ENUM } from "routes/consts";
import BuildingSettings from "../BuildingSettings";
import Tooltip from "components/Tooltip";
import { buildingSettingsStore } from "../BuildingSettings/BuildingSettingsStore";
import { useFeatureAccess } from "hooks/useFeatureAccess";
import { LicenseFeatureEnum } from "api-client";

interface BuildingHeaderSettingsProps {
  setOpenShare: Dispatch<SetStateAction<boolean>>;
}

export default observer(function BuildingHeaderSettings(
  props: BuildingHeaderSettingsProps
) {
  const { t } = useTranslation();
  const { isSharedBuilding, sharedBuildings, currentBuilding } = buildingStore;
  const navigate = useNavigate();
  const toast = useToast();
  const [openDelete, setOpenDelete] = React.useState(false);
  const [openArchive, setOpenArchive] = React.useState(false);
  const [openLeave, setOpenLeave] = React.useState(false);
  const calculationsInProgress = buildingStore.calculationsStatus.inProgress;
  const isUserAdmin =
    sidebarMenuStore.currentOrganisation?.role === USER_ROLES[0].id;
  const isArchived = buildingStore.isCurrentBuildingArchived;
  const { hasFeature } = useFeatureAccess();
  const canArchive = hasFeature(LicenseFeatureEnum.ArchiveBuilding);

  const actionMenu = {
    items: [
      <Tooltip
        content={
          calculationsInProgress
            ? t("building.editBuildingDisabledTooltip")
            : ""
        }
      >
        <span key={0}>{t("buildingPassport.editBuilding")}</span>
      </Tooltip>,
      <span key={1}>{t("buildingPassport.deleteBuilding")}</span>,
      ...(canArchive
        ? [
            <Tooltip
              key="archive"
              content={
                isArchived
                  ? t("buildingSettings.archiveAlready")
                  : !isUserAdmin
                  ? t("buildingSettings.archiveAdminOnly")
                  : ""
              }
            >
              <span>{t("buildingPassport.archiveBuilding")}</span>
            </Tooltip>,
          ]
        : []),
    ],
    itemsDisabled: [
      calculationsInProgress,
      false,
      ...(canArchive ? [!isUserAdmin || isArchived] : []),
    ],
  };

  const actionMenuSharedBuildings = {
    items: [<span key={0}>{t("shareBuilding.leaveBuilding")}</span>],
    itemsDisabled: [false],
  };

  const { items, itemsDisabled } = isSharedBuilding
    ? actionMenuSharedBuildings
    : actionMenu;

  const actions = isSharedBuilding
    ? [() => setOpenLeave(true)]
    : [
        () => buildingSettingsStore.setOpenEditBuilding(true),
        () => setOpenDelete(true),
        ...(canArchive ? [() => setOpenArchive(true)] : []),
      ];

  if (isMobileSize() && !isSharedBuilding) {
    items.push(
      <span key={3} className="sm:hidden">
        {t("buildingPassport.shareBuilding")}
      </span>
    );
    actions.push(() => props.setOpenShare(true));
  }

  const leaveBuilding = async () => {
    if (!currentBuilding) return;
    const userBuildingId = sharedBuildings.items.find(
      (building) => building.building.id === currentBuilding.id
    )?.id;
    const authHeader = getLocalAuthHeader();
    if (userBuildingId) {
      await userBuildingApi
        .concularApiV1RoutersUserBuildingsLeaveBuilding(
          userBuildingId,
          authHeader
        )
        .then((response) => {
          if (response.status === 200) {
            toast(t("shareBuilding.successfullyLeftBuilding"), "success");
            buildingStore.getSharedBuildings();
            navigate(`/${ROUTES_ENUM.DASHBOARD}`, { replace: true });
          }
        })
        .catch((error) => {
          toast(t("shareBuilding.errorLeavingBuilding"), "warning");
          console.error("leaveBuilding.error", error);
        });
    }
  };

  return (
    <>
      <ActionMenu
        items={items}
        itemDisabled={itemsDisabled}
        onSelect={(index) => actions[index]()}
        icon={
          <DotsVerticalIcon
            width={24}
            className="text-gray-500 hover:text-black w-6 h-6"
          />
        }
        itemsClassName={"!min-w-[195px] cursor-pointer top-8"}
      />
      {buildingSettingsStore.openEditBuilding && (
        <BuildingSettings
          open={buildingSettingsStore.openEditBuilding}
          close={() => buildingSettingsStore.setOpenEditBuilding(false)}
          type="edit"
        />
      )}
      {openDelete && <DeleteBuilding open={openDelete} close={setOpenDelete} />}
      {openArchive && (
        <ArchiveBuilding open={openArchive} close={setOpenArchive} />
      )}
      {openLeave && (
        <ConfirmModal
          open={openLeave}
          close={() => setOpenLeave(false)}
          title={t("shareBuilding.wantToLeaveBuilding")}
          descriptionMesage={t("shareBuilding.leaveBuildingWarningMessage")}
          confirmButtonTitle={t("shareBuilding.leaveBuilding")}
          onConfirm={leaveBuilding}
        />
      )}
    </>
  );
});
